import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { BaseComponent } from '@core/components/base-component';
import { TargetedPointerEvent } from '@core/helpers/events/targeted-event';
import { SortHelper } from '@core/helpers/sort.helper';
import { BudgetTypeEnum } from '@enums/BudgetType';
import { DepartmentEstimate } from '@interfaces/DepartmentEstimate';
import { Estimate } from '@interfaces/Estimate';
import { SectionFilterItem, SECTION_FILTERS_STORAGE_KEY } from '@models/consts/estimate-section-filter-items.const';
import { DepartmentVM } from '@models/departmentVM';
import { inOutAnimation } from '@shared/animations';
import { EstimateService } from '@shared/services/estimate.service';

@Component({
  selector: 'pop-grid-display-filter',
  templateUrl: './grid-display-filter.component.html',
  styleUrls: ['./grid-display-filter.component.less'],
  animations: [inOutAnimation]
})
export class GridDisplayFilterComponent extends BaseComponent implements OnInit, AfterViewInit {
  @Input() public set sectionFilterItems(value: SectionFilterItem[]) {
    this.currentSectionFilters = value;
  }
  @Output() public sectionFilterItemsChange = new EventEmitter<SectionFilterItem[]>();

  private departmentVMs: DepartmentVM[] = [];
  public filteredDepartmentVMs: DepartmentVM[] = [];
  public showFilter = false;
  public isDetailedBudgetType = false;
  public currentSectionFilters: SectionFilterItem[] = [];
  public menuHeight = 0;

  public constructor(private estimateService: EstimateService) {
    super();
  }

  @ViewChild('displayOptions', { static: false }) private displayOptions: ElementRef<HTMLDivElement>;
  @ViewChild('displayButton', { static: false }) private displayButton: ElementRef<HTMLButtonElement>;

  @HostListener('window:resize', ['$event'])
  public determineMenuHeight() {
    const minHeight = 150;
    const bottomOffset = 50; //Some margin from bottom
    const { top = 0, height = 0 } = this.displayButton?.nativeElement?.getBoundingClientRect() ?? {};

    this.menuHeight = Math.max(minHeight, window.innerHeight - top - height - bottomOffset);
  }

  public ngOnInit(): void {
    this.subscribe(
      this.estimateService.observeCurrentEstimate().subscribe(currentEstimate => {
        const departmentEstimates = currentEstimate.departmentEstimates.slice();

        const departmentCodes = this.getDepartmentCodesFromEstimateLines(currentEstimate);
        this.isDetailedBudgetType = currentEstimate.budgetType === BudgetTypeEnum.Detailed;
        this.createDepartmentVMs(departmentEstimates, departmentCodes);
      })
    );
  }

  public ngAfterViewInit(): void {
    this.determineMenuHeight();
  }

  public query(event: string) {
    const filterText = event.toLowerCase();

    this.filteredDepartmentVMs = this.departmentVMs.filter(
      option =>
        option.department.description.toLowerCase().includes(filterText) || option.department.code.includes(filterText)
    );
  }

  public selectAll(event: Event) {
    const input = event.target as HTMLInputElement;
    const isChecked = input.checked;
    if (!isChecked) {
      for (const dept of this.departmentVMs) {
        dept.isSelected = false;
        sessionStorage.setItem(dept.department.id, dept.department.code);
      }
      input.checked = false;
      sessionStorage.setItem('dept_select_all', 'false');
    } else {
      for (const dept of this.departmentVMs) {
        dept.isSelected = true;
        sessionStorage.removeItem(dept.department.id);
      }
      input.checked = true;
      sessionStorage.setItem('dept_select_all', 'true');
    }
  }
  private getDepartmentCodesFromEstimateLines(currentEstimate: Estimate) {
    const departmentCodes: string[] = [];
    for (const structure of currentEstimate.structures) {
      for (const line of structure.estimateLines) {
        if (line.departmentEstimate && line.departmentEstimate.department)
          departmentCodes.push(line.departmentEstimate.department.code);
      }
    }
    return departmentCodes;
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: TargetedPointerEvent) {
    if (
      this.showFilter &&
      !this.displayButton?.nativeElement?.contains(event.target) &&
      !this.displayOptions?.nativeElement?.contains(event.target)
    ) {
      this.showFilter = false;
    }
  }

  public onSelect(event: Event, dept: DepartmentVM) {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (!isChecked) {
      sessionStorage.setItem(dept.department.id, dept.department.code);
      return;
    }
    sessionStorage.removeItem(dept.department.id);
  }

  private createDepartmentVMs(departmentEstimates: DepartmentEstimate[], departmentCodes: string[]) {
    const departmentVMs: DepartmentVM[] = [];
    for (const departmentEstimate of departmentEstimates) {
      if (!departmentEstimate.department) {
        return;
      }

      let isSelected = false;

      for (const line of departmentCodes) {
        if (line === sessionStorage.getItem(departmentEstimate.department.id)) {
          isSelected = false;
        } else if (departmentEstimate.department.code === line) {
          isSelected = true;
        }
      }

      const deptVM = new DepartmentVM(departmentEstimate.department, departmentEstimate);
      deptVM.isSelected = isSelected;
      departmentVMs.push(deptVM);

      SortHelper.sortArrayByProperty(departmentVMs, o => o.department?.code);
    }
    this.filteredDepartmentVMs = departmentVMs;
    this.departmentVMs = departmentVMs;
  }

  public saveChanges() {
    this.estimateService.saveChanges().subscribe();
    this.estimateService.dialogEmitChanges();
    this.showFilter = false;

    const storedSectionFilters = this.currentSectionFilters.reduce(
      (acc, curr) => ({ ...acc, [curr.key]: curr.selected }),
      {}
    );
    localStorage.setItem(SECTION_FILTERS_STORAGE_KEY, JSON.stringify(storedSectionFilters));
    this.sectionFilterItemsChange.emit(this.currentSectionFilters);
  }

  public toggleFilterDisplay() {
    this.showFilter = !this.showFilter;
  }
}
